<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import { isPhoneE164OrEmpty } from 'shared/helpers/Validators';

import InboxesAPI from 'dashboard/api/inboxes';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'create',
    validator: value => ['create', 'convert'].includes(value),
  },
  inbox: {
    type: Object,
    default: null,
  },
});

const router = useRouter();
const store = useStore();
const { t } = useI18n();

const isConvertMode = computed(() => props.mode === 'convert');
const inboxName = ref(isConvertMode.value ? props.inbox?.name || '' : '');
const phoneNumber = ref(
  isConvertMode.value ? props.inbox?.phone_number || '' : ''
);
const phoneNumberId = ref(
  isConvertMode.value ? props.inbox?.provider_config?.phone_number_id || '' : ''
);
const businessAccountId = ref(
  isConvertMode.value
    ? props.inbox?.provider_config?.business_account_id ||
      props.inbox?.provider_config?.waba_id ||
      ''
    : ''
);
const apiKey = ref('');
const phoneNumbers = ref([]);
const isLoadingNumbers = ref(false);
const hasLoadedNumbers = ref(false);

const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);

const rules = computed(() => ({
  inboxName: { required },
  phoneNumber: { required, isPhoneE164OrEmpty },
  apiKey: { required },
  phoneNumberId: { required },
}));

const v$ = useVuelidate(rules, {
  inboxName,
  phoneNumber,
  apiKey,
  phoneNumberId,
});

const normalizePhoneNumbers = response => {
  const payload = response?.data || {};
  const records = Array.isArray(payload)
    ? payload
    : payload.phone_numbers || payload.data || payload.payload || [];
  if (!Array.isArray(records)) return [];

  return records
    .map(number => ({
      id: number.id || number.phone_number_id || number.phoneNumberId || '',
      phoneNumber:
        number.phone_number ||
        number.phoneNumber ||
        number.display_phone_number ||
        number.displayPhoneNumber ||
        '',
      name:
        number.name ||
        number.verified_name ||
        number.verifiedName ||
        number.display_phone_number ||
        number.displayPhoneNumber ||
        '',
      businessAccountId:
        number.business_account_id ||
        number.businessAccountId ||
        number.waba_id ||
        number.wabaId ||
        '',
      status: number.status || number.connection_status || number.connectionStatus,
    }))
    .filter(number => number.id && number.phoneNumber)
    .filter(
      number =>
        !number.status || String(number.status).toUpperCase() === 'CONNECTED'
    );
};

const loadPhoneNumbers = async () => {
  v$.value.apiKey.$touch();
  if (v$.value.apiKey.$invalid) return;

  isLoadingNumbers.value = true;
  hasLoadedNumbers.value = false;
  phoneNumbers.value = [];

  try {
    const response = await InboxesAPI.listYcloudPhoneNumbers(apiKey.value.trim());
    phoneNumbers.value = normalizePhoneNumbers(response);
    hasLoadedNumbers.value = true;

    if (phoneNumbers.value.length === 0) {
      useAlert(t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.NO_NUMBERS'));
    }
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        error.message ||
        t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.LOAD_ERROR')
    );
  } finally {
    isLoadingNumbers.value = false;
  }
};

const selectPhoneNumber = number => {
  phoneNumberId.value = number.id;
  phoneNumber.value = number.phoneNumber;
  businessAccountId.value = number.businessAccountId;
};

const handlePhoneNumberChange = () => {
  const number = phoneNumbers.value.find(
    candidate => candidate.id === phoneNumberId.value
  );
  if (number) selectPhoneNumber(number);
};

const buildProviderConfig = () => ({
  api_key: apiKey.value.trim(),
  phone_number_id: phoneNumberId.value,
  business_account_id: businessAccountId.value,
});

const createChannel = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    if (isConvertMode.value) {
      await store.dispatch('inboxes/convertProvider', {
        inboxId: props.inbox.id,
        provider: 'ycloud',
        providerConfig: buildProviderConfig(),
      });

      useAlert(t('INBOX_MGMT.CONVERT.API.SUCCESS_MESSAGE'));
      router.replace({
        name: 'settings_inbox_show',
        params: {
          accountId: router.currentRoute.value.params.accountId,
          inboxId: props.inbox.id,
        },
      });
      return;
    }

    const whatsappChannel = await store.dispatch('inboxes/createChannel', {
      name: inboxName.value.trim(),
      channel: {
        type: 'whatsapp',
        phone_number: phoneNumber.value,
        provider: 'ycloud',
        provider_config: buildProviderConfig(),
      },
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: {
        page: 'new',
        inbox_id: whatsappChannel.id,
      },
    });
  } catch (error) {
    useAlert(
      error.message ||
        t(
          isConvertMode.value
            ? 'INBOX_MGMT.CONVERT.API.ERROR_MESSAGE'
            : 'INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE'
        )
    );
  }
};
</script>

<template>
  <form class="flex flex-wrap flex-col mx-0" @submit.prevent="createChannel">
    <label :class="{ error: v$.inboxName.$error }">
      {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
      <input
        v-model="inboxName"
        type="text"
        :disabled="isConvertMode"
        :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
        @blur="v$.inboxName.$touch"
      />
      <span v-if="v$.inboxName.$error" class="message">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.ERROR') }}
      </span>
    </label>

    <label :class="{ error: v$.apiKey.$error }">
      {{ $t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.API_KEY_LABEL') }}
      <input
        v-model="apiKey"
        type="password"
        autocomplete="new-password"
        :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.API_KEY_PLACEHOLDER')"
        @blur="v$.apiKey.$touch"
      />
      <span class="mt-1 text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.API_KEY_HELP') }}
      </span>
      <span v-if="v$.apiKey.$error" class="message">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.API_KEY_ERROR') }}
      </span>
    </label>

    <div class="flex gap-2 items-end">
      <label class="flex-1">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.NUMBER_LABEL') }}
        <select
          v-model="phoneNumberId"
          :disabled="isLoadingNumbers || phoneNumbers.length === 0"
          @change="handlePhoneNumberChange"
        >
          <option value="" disabled>
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.NUMBER_PLACEHOLDER') }}
          </option>
          <option
            v-for="number in phoneNumbers"
            :key="number.id"
            :value="number.id"
          >
            {{ number.name }} ({{ number.phoneNumber }})
          </option>
        </select>
        <span v-if="v$.phoneNumberId.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.NUMBER_ERROR') }}
        </span>
      </label>
      <NextButton
        type="button"
        outline
        slate
        :is-loading="isLoadingNumbers"
        :label="$t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.LOAD_NUMBERS')"
        @click="loadPhoneNumbers"
      />
    </div>
    <p v-if="hasLoadedNumbers && phoneNumbers.length === 0" class="text-sm text-n-slate-11">
      {{ $t('INBOX_MGMT.ADD.WHATSAPP.YCLOUD.NO_NUMBERS') }}
    </p>

    <input v-model="phoneNumber" type="hidden" />

    <div class="w-full mt-4">
      <NextButton
        :is-loading="uiFlags.isCreating || uiFlags.isUpdating"
        type="submit"
        solid
        blue
        :label="
          isConvertMode
            ? $t('INBOX_MGMT.CONVERT.SUBMIT_BUTTON')
            : $t('INBOX_MGMT.ADD.WHATSAPP.SUBMIT_BUTTON')
        "
      />
    </div>
  </form>
</template>
